// src/pages/CostEstimatePage.tsx
//
// ★ PHASE 10 — "Financial Counselling Access and Cost
// Estimation".
//
// ⚠ NOTHING here is a bill. `POST /api/cost-estimate` reads no `app.bill` row
// and creates none; every result on this screen is labelled ESTIMATE, and the
// disclaimer the gateway sends is rendered verbatim rather than paraphrased.
//
// ⚠ Resolves on the DATE OF MEDICAL SERVICE — this form has no submission-date
// field at all, the same discipline `EntitlementPage.tsx` and
// `ReferenceDataPage.tsx` both already carry.
//
// ⚠ A ward-based scheme (CPW/FAW/CWM) with no confirmed rate in
// `app.ward_rule_version` REFUSES rather than guesses — this screen renders
// that refusal exactly as the gateway worded it, never a substituted number.
//
// ══ ★★ WAVE 9 — STORY 51's THIRD ACCEPTANCE CRITERION: THE SIDE-BY-SIDE ══
//
// The RTM row for this story said, as its stated narrowing: *"'dual benefits
// comparison' and 'ward class comparison data' (the story's third acceptance
// criterion) are NOT built — a caller wanting a second scenario calls the endpoint
// again with different inputs; there is no side-by-side rendering."* Both halves
// were true. The second one is what this screen now does — and the first half is
// exactly HOW it does it.
//
// ⚠ **NO SERVER CHANGE, and that is the constraint that shaped this.** A
// comparison endpoint would be a second entry point into the money chain, and
// `estimate.ts` exists precisely because *"reuses `processBill`/
// `computeWardSchemeBill` VERBATIM, no second engine"* was the requirement. So the
// comparison is TWO CALLS to the one endpoint, rendered in two columns by one
// renderer, and every figure in both columns is the server's own.
//
// ⚠ **THE DIFFERENCE ROW IS THE ONLY ARITHMETIC THIS SCREEN DOES**, and it is an
// integer subtraction of two server-supplied cents values — never a re-derivation
// of a split, never a rate applied here. It is labelled as a difference between two
// estimates rather than presented as a third estimate, because a counselling
// conversation is exactly where a number that looks authoritative and was computed
// in a browser would do the most damage.
//
// ⚠ **A REFUSED SECOND SCENARIO IS SHOWN, NOT HIDDEN.** A ward class with no
// confirmed rate refuses, and the whole point of comparing is that the officer
// learns which options are answerable. A column that vanished would read as "the
// two are the same"; a column carrying the server's refusal reads as what it is.
// The difference row then states that it cannot be computed and why.
import { useId, useState, type FormEvent } from 'react'
import { SgdsButton, SgdsInput } from '@govtechsg/sgds-web-component/react'
import { api, ApiError } from '../lib/api'
import type { CostEstimateResponse } from '../lib/types'
import { useApi } from '../lib/useApi'
import { ServiceDatePicker } from '../components/ServiceDatePicker'
import { FieldError, centsProblem, fieldErrorProps, parseCents } from '../components/FieldError'
import { EmptyCard, ErrorCard, LoadingCard } from '../components/StateCards'
import { formatCents } from '../lib/format'
import { targetValue } from '../lib/sgds-dom'
import { RateProvenanceNote } from '../components/RateProvenance'

interface EstimateRequest {
  readonly officerId: string
  readonly serviceDate: string
  readonly billAmountCents: number
  readonly wardClass?: string
}

/**
 * ★★ WAVE 9 — one scenario's outcome: the estimate, or the server's refusal.
 *
 * ⚠ A discriminated pair rather than `data | null`, because the comparison has to
 * be able to say *"this ward class cannot be estimated, and here is what the server
 * said"* in the column where that scenario would have been. `useApi`'s single
 * error channel cannot express "A computed and B refused", which is the case that
 * matters most: an officer comparing wards is deciding, and a silently dropped
 * column is a worse answer than a refused one.
 */
interface Scenario {
  readonly label: string
  readonly wardClass: string | undefined
  readonly estimate: CostEstimateResponse | null
  readonly refusal: string | null
}

/**
 * Both scenarios, in one fetch, so the pair is always internally consistent.
 *
 * ⚠ **`Promise.allSettled`, not `Promise.all`.** With `all`, one refused scenario
 * rejects the whole pair and the officer loses the answer that DID compute — which
 * is the opposite of what a comparison is for. Each scenario carries its own
 * outcome and the renderer shows both.
 *
 * ⚠ Both calls go to the SAME endpoint with the same officer, service date and
 * amount, differing only in `wardClass`. That is what makes the difference row
 * meaningful: one variable moves.
 */
async function loadScenarios(
  base: EstimateRequest,
  comparisonWardClass: string | undefined,
): Promise<readonly Scenario[]> {
  const requests: readonly { label: string; wardClass: string | undefined }[] =
    comparisonWardClass === undefined
      ? [{ label: base.wardClass ?? 'As entitled', wardClass: base.wardClass }]
      : [
          { label: base.wardClass ?? 'As entitled', wardClass: base.wardClass },
          { label: comparisonWardClass, wardClass: comparisonWardClass },
        ]

  const settled = await Promise.allSettled(
    requests.map((r) =>
      api.costEstimate({
        officerId: base.officerId,
        serviceDate: base.serviceDate,
        billAmountCents: base.billAmountCents,
        ...(r.wardClass !== undefined && { wardClass: r.wardClass }),
      }),
    ),
  )

  return requests.map((r, index) => {
    const outcome = settled[index]
    if (outcome !== undefined && outcome.status === 'fulfilled') {
      return { label: r.label, wardClass: r.wardClass, estimate: outcome.value, refusal: null }
    }
    const reason = outcome !== undefined && outcome.status === 'rejected' ? outcome.reason : undefined

    /*
      ★★★ A SERVER REFUSAL BELONGS IN THE COLUMN; A TRANSPORT FAILURE DOES NOT, AND
      COLLAPSING THE TWO WOULD HAVE MADE THIS SCREEN LIE.

      ⚠ The first version of this function swallowed EVERY rejection into a per-
      scenario `refusal`, which had two consequences and both were wrong. It made
      the page's top-level `ErrorCard` unreachable — dead code carrying a comment
      claiming to handle refusals, which is the defect class this repository logs
      repeatedly — and, worse, it rendered a dropped connection as *"ward class B2
      was refused"*, i.e. a statement about the Authority's rate table that the
      server never made.

      So an `ApiError` (the gateway answered, and refused, in its own words) is a
      scenario outcome, and anything else is RETHROWN for `useApi` to surface as
      what it is: this screen could not reach the server, which is not an answer
      about a ward class at all.
    */
    if (reason instanceof ApiError) {
      return { label: r.label, wardClass: r.wardClass, estimate: null, refusal: reason.message }
    }
    throw reason instanceof Error
      ? reason
      : new Error('The cost estimate request failed and produced no error to report.')
  })
}

/**
 * ★ Whole cents, signed, so a difference reads as a direction and not just a size.
 *
 * ⚠ `formatCents` takes a magnitude and the sign has to be carried explicitly: an
 * unsigned "$300.00" in a row headed "change in co-payment" is ambiguous in the one
 * direction that matters to the officer paying it. `+`/`−` and an explicit "no
 * change" for zero — never a bare "0.00", which reads as an unfilled field.
 */
function signedCents(cents: number): string {
  if (cents === 0) return 'No change'
  return `${cents > 0 ? '+' : '\u2212'}${formatCents(Math.abs(cents))}`
}

/**
 * ★★ ONE renderer for one scenario, used for the single estimate AND for each
 * column of the comparison.
 *
 * ⚠ **One renderer and not two, deliberately.** Two would drift: the single view
 * would keep the disclaimer and the provenance note while the comparison columns
 * quietly lost them, which is precisely the surface where an unlabelled fabricated
 * rate does the most damage (see `RateProvenanceNote`'s own header). Every column
 * carries the server's disclaimer, its applied rule and its provenance, or its
 * refusal — never a subset chosen for layout.
 */
function ScenarioCard({ scenario, comparing }: { scenario: Scenario; comparing: boolean }) {
  if (scenario.estimate === null) {
    return (
      <div className="pt-card border-l-4 border-l-[var(--sgds-danger-border-color-emphasis)]">
        <p className="uppercase tracking-wide text-xs font-bold text-[var(--sgds-color-subtle)] mb-2">
          {comparing ? `Ward class ${scenario.label}` : 'Estimate'}: refused
        </p>
        {/*
          ⚠ The SERVER's wording, verbatim. A ward-based scheme with no confirmed
          rate in `app.ward_rule_version` refuses rather than guessing, and this
          screen has never substituted a number for that refusal — the comparison
          columns hold to the same rule.
        */}
        <p className="text-sm">{scenario.refusal}</p>
        <p className="text-xs text-[var(--sgds-body-color-subtle)] mt-3">
          No figure is shown for this ward class. An estimate is refused rather than guessed.
        </p>
      </div>
    )
  }

  const estimate = scenario.estimate
  return (
    <div className="pt-card border-l-4 border-l-[var(--sgds-warning-border-color-emphasis)]">
      <p className="uppercase tracking-wide text-xs font-bold text-[var(--sgds-warning-color-fixed-dark)] mb-2">
        {comparing ? `Ward class ${scenario.label}: estimate, not a computed bill` : 'Estimate, not a computed bill'}
      </p>
      <p className="text-sm text-[var(--sgds-body-color-subtle)] mb-4">{estimate.disclaimer}</p>

      <dl className="grid grid-cols-2 gap-x-6 gap-y-3 max-w-lg">
        <dt className="text-sm text-[var(--sgds-body-color-subtle)]">Scheme</dt>
        <dd className="pt-tabular">{estimate.scheme ?? 'None'}</dd>
        <dt className="text-sm text-[var(--sgds-body-color-subtle)]">Ward class</dt>
        <dd className="pt-tabular">{estimate.wardClass ?? 'Not applicable'}</dd>
        <dt className="text-sm text-[var(--sgds-body-color-subtle)]">Bill amount</dt>
        <dd className="pt-tabular">{formatCents(estimate.billAmountCents)}</dd>
        <dt className="text-sm font-semibold">Estimated government subsidy</dt>
        <dd className="pt-tabular font-semibold">{formatCents(estimate.subsidyCents)}</dd>
        <dt className="text-sm font-semibold">Estimated officer co-payment</dt>
        <dd className="pt-tabular font-semibold">{formatCents(estimate.coPaymentCents)}</dd>
      </dl>

      <p className="text-xs text-[var(--sgds-body-color-subtle)] mt-4">
        Applied rule {estimate.appliedRule.id} v{estimate.appliedRule.version}, in force from{' '}
        {estimate.appliedRule.serviceDate}.
      </p>

      {/*
        ⚠ THE ESTIMATE IS THE SURFACE WHERE AN INVENTED RATE DOES THE MOST DAMAGE. A
        bill detail describes money that has already moved; this describes money an
        officer is about to make a decision about, in a counselling conversation.
        Every word is the server's — see `components/RateProvenance.tsx` — and it is
        `null` for MSO/CCS and for any rate the Authority supplies, so nothing
        renders on those. ★ In a COMPARISON it renders per column, because the two
        ward classes can be priced by different rules and only one of them may be
        fabricated.
      */}
      <RateProvenanceNote provenance={estimate.rateProvenance} heading="Where this subsidy rate came from" />
    </div>
  )
}

export function CostEstimatePage() {
  const dateId = useId()
  const billAmountErrorId = `${useId()}-bill-amount-error`
  const [officerId, setOfficerId] = useState('')
  const [serviceDate, setServiceDate] = useState('')
  const [billAmount, setBillAmount] = useState('')
  const [wardClass, setWardClass] = useState('')
  const [comparisonWardClass, setComparisonWardClass] = useState('')
  const [submitted, setSubmitted] = useState<EstimateRequest | null>(null)
  const [submittedComparison, setSubmittedComparison] = useState<string | undefined>(undefined)

  /**
   * ★★ WAVE 9 — ONE fetch producing one or two scenarios.
   *
   * ⚠ Keyed on the comparison ward class as well, so adding or clearing it
   * re-queries rather than leaving a stale second column beside a fresh first one.
   * A comparison whose two halves came from different requests is the one thing
   * this screen must never render, because the whole claim it makes is that only
   * the ward class differs.
   */
  const estimate = useApi(
    () => (submitted === null ? Promise.resolve(null) : loadScenarios(submitted, submittedComparison)),
    [
      submitted?.officerId,
      submitted?.serviceDate,
      submitted?.billAmountCents,
      submitted?.wardClass,
      submittedComparison,
    ],
  )

  const scenarios = estimate.data ?? null
  const comparing = scenarios !== null && scenarios.length > 1

  /**
   * ★★★ THE DIFFERENCE — the only arithmetic on this screen, and it is an integer
   * subtraction of two figures the SERVER produced.
   *
   * ⚠ It is `null` unless BOTH scenarios computed. A difference against a refused
   * scenario would be a number derived from an absence, and on a counselling screen
   * that is the worst available output: it looks like an answer. When one side
   * refuses, the row says it cannot be computed and the refused column carries the
   * server's own words.
   *
   * ⚠ Integers throughout — `Cents` in, `Cents` out, no division and no rate. This
   * screen re-derives no split; `packages/reporting/src/money.ts`'s header records
   * why a float anywhere near money is refused in this product, and the safest way
   * to honour that here is to do no arithmetic beyond a subtraction.
   */
  const difference =
    scenarios !== null && scenarios.length === 2 && scenarios[0]?.estimate != null && scenarios[1]?.estimate != null
      ? {
          subsidyCents: scenarios[1].estimate.subsidyCents - scenarios[0].estimate.subsidyCents,
          coPaymentCents: scenarios[1].estimate.coPaymentCents - scenarios[0].estimate.coPaymentCents,
        }
      : null

  /**
   * ★★ THE AMOUNT, PARSED HERE RATHER THAN ROUNDED AT SUBMIT — and the check
   * this replaces was DEAD.
   *
   * ⚠ It read `Number.isFinite(Number(billAmount)) && parsedDollars >= 0`
   * against a `type="number"` field, and a number input had already made the
   * value either a valid float string or EMPTY: neither branch could ever be
   * false in a browser. What the officer actually got for `abc` or `1e999` was a
   * box still showing what they typed, a disabled Estimate button, and no
   * sentence anywhere. `FieldError`'s header carries the Chromium measurements.
   *
   * ⚠ And `Math.round(parsedDollars * 100)` SILENTLY ROUNDED a third decimal
   * place — an estimate quoted for an amount the officer never typed. Refused
   * now, by the same one parser `ReferenceDataPage` uses.
   */
  const parsedAmount = parseCents(billAmount)
  const billAmountProblem = centsProblem(billAmount, 'Estimated bill amount')
  const canSubmit =
    officerId.trim().length > 0 &&
    serviceDate.length > 0 &&
    // ⚠ Reads the PARSER, not `billAmountProblem` — the latter is suppressed
    // while the field is untouched, and a button armed on an empty required
    // field is exactly what that suppression could otherwise cause.
    'cents' in parsedAmount

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    if (!canSubmit || !('cents' in parsedAmount)) return
    setSubmitted({
      officerId: officerId.trim(),
      serviceDate,
      billAmountCents: parsedAmount.cents,
      ...(wardClass.trim().length > 0 && { wardClass: wardClass.trim() }),
    })
    // ⚠ Frozen at submit, exactly like the rest of the request. A comparison field
    // edited after Estimate must not change the columns already on screen —
    // `ReferenceDataPage`'s `PendingWrite` records the same argument at length for
    // its confirmation dialog, and the reason is identical: what is described has
    // to be what was asked for.
    setSubmittedComparison(comparisonWardClass.trim().length > 0 ? comparisonWardClass.trim() : undefined)
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-3xl mb-2">Financial counselling: cost estimate</h1>
        <p className="text-[var(--sgds-body-color-subtle)] max-w-[70ch]">
          A pre-admission ESTIMATE of the government subsidy and co-payment split for a
          beneficiary&rsquo;s medical bill, resolved on the <strong>date of medical service</strong>, never
          today&rsquo;s date, and never the date the bill would later be submitted. This is not a bill: nothing here
          is recorded, and no bill is created.
        </p>
      </div>

      <form className="pt-card flex flex-col gap-5" onSubmit={handleSubmit}>
        <SgdsInput
          label="Officer ID"
          value={officerId}
          onSgdsInput={(e) => setOfficerId(targetValue(e))}
          placeholder="e.g. OFF-01"
        />

        <div>
          <span id={dateId} className="block text-sm font-semibold mb-1">
            Date of medical service <span aria-hidden="true">*</span>
            <span className="sr-only">required</span>
          </span>
          <ServiceDatePicker value={serviceDate} onChange={setServiceDate} labelledBy={dateId} />
        </div>

        {/*
          ⚠ **A PLAIN input, not `SgdsInput`, and `type="text"` not
          `type="number"` — both for stated reasons.**

          `type="text"`: a number input discards `abc` and `1e999` before this
          screen sees them, so the only thing left to show the officer was a
          disabled button. `inputMode="decimal"` keeps the numeric keypad.

          A plain input: `aria-describedby` must name an element in the SAME
          document, and `sgds-input` keeps its input inside a shadow root where
          an id in the light DOM cannot reach it. A field-level error the input
          does not reference is a paragraph, not an error message — the same
          reasoning `WardClassesPage` records for its rank order field.
        */}
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-semibold">
            Estimated bill amount (SGD) <span aria-hidden="true">*</span>
            <span className="sr-only">required</span>
          </span>
          <input
            type="text"
            inputMode="decimal"
            className="pt-native-date"
            value={billAmount}
            required
            placeholder="e.g. 200.00"
            {...fieldErrorProps(billAmountErrorId, billAmountProblem)}
            onChange={(e) => setBillAmount(e.target.value)}
          />
          <FieldError id={billAmountErrorId} problem={billAmountProblem} />
        </label>

        <SgdsInput
          label="Ward class (only if the scheme is ward-based: CPW, FAW or CWM)"
          value={wardClass}
          onSgdsInput={(e) => setWardClass(targetValue(e))}
          placeholder="e.g. B1"
        />

        {/*
          ★★ WAVE 9 — story 51's third acceptance criterion. OPTIONAL, and the hint
          says what it does and what it does not: two calls to the one endpoint, and
          a difference that is a subtraction of the server's own figures.
        */}
        <SgdsInput
          label="Compare with a second ward class (optional)"
          hintText="Estimates both and shows the difference. Same officer, same date, same amount: only the ward class moves."
          value={comparisonWardClass}
          onSgdsInput={(e) => setComparisonWardClass(targetValue(e))}
          placeholder="e.g. B2"
        />

        <div>
          <SgdsButton type="submit" variant="primary" disabled={!canSubmit} loading={estimate.loading}>
            Estimate
          </SgdsButton>
        </div>
      </form>

      {/*
        ⚠ **STILL REACHABLE, and `loadScenarios` is written to keep it so.** This
        card is for a request that never got an answer — the gateway unreachable, the
        session gone. A server REFUSAL is not shown here: it belongs in the column
        for the ward class it refused, in the server's own words, which is what makes
        a comparison with one refused side readable at all. See `loadScenarios`.
      */}
      {estimate.error !== null && (
        <ErrorCard title="Could not reach the estimate service" message={estimate.error} />
      )}

      {/* ★ WAVE 26 — the wait and an empty answer are STATED, as on every other screen. */}
      {estimate.loading && <LoadingCard label="Estimating" rows={3} />}
      {submitted !== null && !estimate.loading && estimate.error === null && scenarios !== null && scenarios.length === 0 && (
        <EmptyCard title="No estimate to show" message="The estimate service answered with no scenarios for this request. Check the officer id and ward class, then estimate again." />
      )}

      {submitted !== null && scenarios !== null && scenarios.length > 0 && (
        <div className="flex flex-col gap-4">
          {comparing && (
            <div className="pt-card">
              <h2 className="text-lg font-semibold mb-1">Ward class comparison</h2>
              <p className="text-sm text-[var(--sgds-body-color-subtle)] max-w-[75ch]">
                Compare two ward classes side by side. Both columns are separate estimates from the
                same endpoint, for the same officer, the same date of medical service and the same bill amount:
                only the ward class differs. <strong>Neither column is recorded and neither is a bill.</strong>
              </p>
            </div>
          )}

          <div className={comparing ? 'grid gap-4 lg:grid-cols-2' : 'flex flex-col gap-4'}>
            {scenarios.map((scenario, index) => (
              <ScenarioCard key={`${scenario.label}-${index}`} scenario={scenario} comparing={comparing} />
            ))}
          </div>

          {comparing && (
            <div className="pt-card">
              <h3 className="text-base font-semibold mb-2">
                Choosing {scenarios[1]?.label} instead of {scenarios[0]?.label}
              </h3>
              {difference === null ? (
                /*
                  ⚠ THE CASE THAT MATTERS. One scenario refused, so there is nothing
                  to subtract — and a zero here would read as "the two are the same",
                  which is a wrong answer in a counselling conversation rather than a
                  missing one. The refused column above carries the server's own
                  words; this says why the comparison itself cannot be made.
                */
                <p className="text-sm">
                  <strong>Cannot be compared.</strong> One of the two ward classes could not be estimated: the
                  column above says what the server refused and why. A difference is not shown rather than shown as
                  zero, because zero would mean the two options cost the same.
                </p>
              ) : (
                <>
                  <dl className="grid grid-cols-2 gap-x-6 gap-y-3 max-w-lg">
                    <dt className="text-sm font-semibold">Change in estimated government subsidy</dt>
                    <dd className="pt-tabular font-semibold">{signedCents(difference.subsidyCents)}</dd>
                    <dt className="text-sm font-semibold">Change in estimated officer co-payment</dt>
                    <dd className="pt-tabular font-semibold">{signedCents(difference.coPaymentCents)}</dd>
                  </dl>
                  {/*
                    ⚠ The one sentence this screen owes a reader about its own
                    arithmetic. Both figures above are the SERVER's; the difference is
                    a subtraction of two integers and nothing else — no rate is
                    applied here and no split is re-derived.
                  */}
                  <p className="text-xs text-[var(--sgds-body-color-subtle)] mt-4">
                    Each column is the server&rsquo;s own estimate. This row is the difference between the two:
                    a plain subtraction of the two amounts, not a third estimate: no rate is applied and no split is
                    recalculated on this screen.
                  </p>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
